import { useCallback } from 'react'
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom'

import {
  calendarMonth,
  calendarWeek,
  newCalendarAllChannels,
  newCalendarSingleChannel,
  scheduleCalendar,
} from '~publish/legacy/routes'
import { isPublicPublishPath } from '~publish/helpers/publicPublishPaths'

/**
 * Represents the view mode of the calendar.
 */
export type CalendarDateRangeViewMode = 'week' | 'month' | 'three-day'

export const VIEW_MODE_LABELS: Record<CalendarDateRangeViewMode, string> = {
  'three-day': '3 Days',
  week: 'Week',
  month: 'Month',
}

export function isCalendarViewMode(
  value: string,
): value is CalendarDateRangeViewMode {
  return value === 'week' || value === 'month' || value === 'three-day'
}

export const VIEW_MODE_OPTIONS: Array<[CalendarDateRangeViewMode, string]> =
  Object.keys(VIEW_MODE_LABELS).flatMap((mode) =>
    isCalendarViewMode(mode) ? [[mode, VIEW_MODE_LABELS[mode]]] : [],
  )

/**
 * A custom hook that manages the view mode of the calendar.
 * It handles the routing logic for switching between week and month views,
 * and supports both legacy and new calendar routes.
 *
 * @returns {[CalendarDateRangeViewMode, (mode: CalendarDateRangeViewMode | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode)) => void]}
 * A tuple containing:
 *   - The current view mode ('week' or 'month')
 *   - A function to set the view mode
 */
export const useCalendarDateRangeViewMode = (): [
  CalendarDateRangeViewMode,
  (
    mode:
      | CalendarDateRangeViewMode
      | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode),
  ) => void,
] => {
  const navigate = useNavigate()
  const location = useLocation()

  const isLegacyCalendarWeek = !!useMatch(calendarWeek.route)
  const isLegacyCalendarMonth = !!useMatch(calendarMonth.route)
  const isLegacyView = isLegacyCalendarWeek || isLegacyCalendarMonth
  const isLegacyWeekView = isLegacyCalendarWeek
  const isSingleChannel = !!useMatch(newCalendarSingleChannel.route)

  // New IA routes (CORE-3371)
  const isScheduleCalendar = !!useMatch(scheduleCalendar.route)

  const params = useParams<{
    granularity: CalendarDateRangeViewMode
    id: string
  }>()

  const publicViewMode = new URLSearchParams(location.search).get('granularity')
  const isPublicPublishRoute = isPublicPublishPath(location.pathname)

  const viewMode: CalendarDateRangeViewMode = isPublicPublishRoute
    ? publicViewMode && isCalendarViewMode(publicViewMode)
      ? publicViewMode
      : 'month'
    : isLegacyWeekView
      ? 'week'
      : params.granularity === 'three-day'
        ? 'three-day'
        : params.granularity === 'week'
          ? 'week'
          : 'month'

  /**
   * Navigates to the specified calendar view mode.
   *
   * @param {CalendarDateRangeViewMode} mode - The view mode to navigate to ('week' or 'month')
   */
  const goTo = useCallback(
    (mode: CalendarDateRangeViewMode) => {
      if (isPublicPublishRoute) {
        const searchParams = new URLSearchParams(location.search)
        if (mode === 'month') {
          searchParams.delete('granularity')
        } else {
          searchParams.set('granularity', mode)
        }
        navigate({
          pathname: location.pathname,
          search: searchParams.toString(),
          hash: location.hash,
        })
        return
      }

      let route: string
      if (isLegacyView) {
        // Legacy routes don't support three-day; fall back to week
        if (mode === 'month') {
          route = calendarMonth.getRoute()
        } else {
          route = calendarWeek.getRoute()
        }
      } else if (isScheduleCalendar) {
        route = scheduleCalendar.getRoute(mode, undefined, true)
      } else if (isSingleChannel) {
        route = newCalendarSingleChannel.getRoute(params.id ?? '', mode)
      } else {
        route = newCalendarAllChannels.getRoute(mode, undefined, true)
      }
      navigate(route)
    },
    [
      isLegacyView,
      isPublicPublishRoute,
      isScheduleCalendar,
      isSingleChannel,
      location.hash,
      location.pathname,
      location.search,
      navigate,
      params.id,
    ],
  )

  /**
   * Sets the view mode of the calendar.
   * It can accept either a ViewMode directly, or a function that receives the current ViewMode
   * and returns a new ViewMode.
   *
   * @param {CalendarDateRangeViewMode | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode)} mode - The new view mode or a function to compute it
   */
  const setViewMode = useCallback(
    (
      mode:
        | CalendarDateRangeViewMode
        | ((mode: CalendarDateRangeViewMode) => CalendarDateRangeViewMode),
    ): void => {
      if (typeof mode === 'function') {
        const newViewMode = mode(viewMode)
        goTo(newViewMode)
      } else {
        goTo(mode)
      }
    },
    [goTo, viewMode],
  )

  return [viewMode, setViewMode]
}
